import * as React from 'react';
import { toast } from 'react-hot-toast';
import { useUpdateCreatorProfileMutation } from 'generated/graphql';
import { Modal } from '@design-system/components/modal/modal';
import { Input } from '@design-system/components/input/input';
import { ConfirmationCodeInput } from '@design-system/components/input/confirmationCodeInput';
import { useUserStore } from '@client/new/stores/user.store';
import { sendEmailChallenge, confirmEmailCode } from '@client/new/api/auth.api';
import * as styles from './ApplePrivateRelayModal.css';

type Variant = 'add-email' | 'disconnect';

type Props = {
  isOpen: boolean;
  variant: Variant;
  onClose: () => void;
  onEmailUpdated: () => Promise<void>;
};

type Step = 'email' | 'code';

export const ApplePrivateRelayModal = ({
  isOpen,
  variant,
  onClose,
  onEmailUpdated,
}: Props) => {
  const { user, login } = useUserStore();
  const [updateProfile] = useUpdateCreatorProfileMutation();
  const [step, setStep] = React.useState<Step>('email');
  const [email, setEmail] = React.useState('');
  const [emailError, setEmailError] = React.useState<string | undefined>();
  const [code, setCode] = React.useState('');
  const [codeError, setCodeError] = React.useState<string | undefined>();
  const [isSending, setIsSending] = React.useState(false);
  const [isVerifying, setIsVerifying] = React.useState(false);

  const handleClose = () => {
    setEmail('');
    setEmailError(undefined);
    setCode('');
    setCodeError(undefined);
    setStep('email');
    onClose();
  };

  const handleSendCode = async () => {
    if (!email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    setIsSending(true);
    try {
      await sendEmailChallenge(email);
      setStep('code');
    } catch {
      toast.error('Failed to send verification code');
    } finally {
      setIsSending(false);
    }
  };

  const handleVerifyCode = async (enteredCode: string) => {
    setIsVerifying(true);
    try {
      const result = await confirmEmailCode(email, enteredCode);
      if (result.user) {
        toast.error('The account connected to this email already exists');
        handleClose();
        return;
      }
      await updateProfile({
        variables: {
          data: {
            id: user.influencerId,
            email,
            cascadeToPortalUser: true,
          },
        },
      });
      login({ ...user, email });
      toast.success('Email updated successfully');
      handleClose();
      if (variant === 'disconnect') {
        await onEmailUpdated();
      }
    } catch {
      setCodeError('Invalid code. Please try again.');
      setCode('');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResend = async () => {
    try {
      await sendEmailChallenge(email);
      toast.success('Code sent again');
    } catch {
      toast.error('Failed to resend code');
    }
  };

  if (step === 'code') {
    return (
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title="Enter verification code"
        centerContent
        modalClassName="w-full md:w-[600px]"
      >
        <div className={styles.codeContent}>
          <p className={styles.sentTo}>We&apos;ve sent a code to your email:</p>
          <p className={styles.emailDisplay}>{email}</p>
          <ConfirmationCodeInput
            value={code}
            onChange={setCode}
            onComplete={handleVerifyCode}
            error={codeError}
            disabled={isVerifying}
          />
          <p className={styles.spamNote}>
            Please check your spam if you do not see it in your inbox.
          </p>
          <button className={styles.resendLink} onClick={handleResend}>
            Didn&apos;t receive a code? Send again
          </button>
        </div>
      </Modal>
    );
  }

  const title =
    variant === 'disconnect' ? 'Disconnect account' : 'Add email to log in';

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={title}
      centerContent
      primaryCta={{
        caption: 'Send code',
        onClick: handleSendCode,
        disabled: isSending,
      }}
      secondaryCta={{
        caption: 'Cancel',
        onClick: handleClose,
      }}
      modalClassName="w-full md:w-[600px]"
    >
      <div className={styles.content}>
        {variant === 'disconnect' && (
          <p className={styles.description}>
            To disconnect Apple, you need another way to sign in to your
            account.
          </p>
        )}
        <p className={styles.description}>
          Enter an email address to use for secure login with a verification
          code.
        </p>
        <Input
          type="email"
          placeholder="Email address"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setEmailError(undefined);
          }}
          error={emailError}
        />
      </div>
    </Modal>
  );
};
